import { useEffect, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import type { FailureCase } from "../domain/failure-rules";

interface CaseEditorProps {
  failureCase: FailureCase;
  onSave: (patch: Partial<Pick<FailureCase, "justification" | "accepted" | "reviewed">>) => void;
  onClose: () => void;
}

export function CaseEditor({ failureCase, onSave, onClose }: CaseEditorProps) {
  const [justification, setJustification] = useState(failureCase.justification);
  const [accepted, setAccepted] = useState(failureCase.accepted);
  const [reviewed, setReviewed] = useState(failureCase.reviewed);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      event.stopPropagation();
      onClose();
    }
  }

  function handleSubmit() {
    onSave({ justification, accepted, reviewed });
    onClose();
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- patrón "Escape para cerrar" del panel de edición (04-sistema-visual-y-accesibilidad.md); el foco ya está gestionado en el textarea interactivo.
    <div className="case-editor" onKeyDown={handleKeyDown} role="group" aria-label={`Editar caso ${failureCase.statusCode} — ${failureCase.title}`}>
      <div className="field">
        <label htmlFor={`justification-${failureCase.id}`}>¿Por qué es relevante o por qué no aplica?</label>
        <textarea
          id={`justification-${failureCase.id}`}
          ref={textareaRef}
          value={justification}
          onChange={(event) => setJustification(event.target.value)}
        />
      </div>
      <div className="checkbox-row">
        <input
          type="checkbox"
          id={`accepted-${failureCase.id}`}
          checked={accepted}
          onChange={(event) => setAccepted(event.target.checked)}
        />
        <label htmlFor={`accepted-${failureCase.id}`}>Este caso aplica al contrato real</label>
      </div>
      <div className="checkbox-row">
        <input
          type="checkbox"
          id={`reviewed-${failureCase.id}`}
          checked={reviewed}
          onChange={(event) => setReviewed(event.target.checked)}
        />
        <label htmlFor={`reviewed-${failureCase.id}`}>Confirmo este supuesto tras revisarlo (cuenta para la cobertura)</label>
      </div>
      <div className="action-row">
        <button type="button" className="button button-primary button-small" onClick={handleSubmit}>
          Guardar
        </button>
        <button type="button" className="button button-secondary button-small" onClick={onClose}>
          Cancelar (Esc)
        </button>
      </div>
    </div>
  );
}
